import { BulkMenu, ExportViewButton, FilterButton, ViewTabs, customColumns, useProperties, useRecordList } from './records';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase } from 'lucide-react';
import { DataTable, type Column } from '@/components/shared/data-table';
import { ObjectIndex, FilterPill, AddButton } from '@/components/shared/object-index';
import { BulkDelete, BulkExport } from '@/components/shared/bulk';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSession } from '@/features/auth/use-session';
import { useDebounce } from '@/hooks/use-debounce';
import { usePermission } from '@/hooks/use-permission';
import { formatDate, formatMoney } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useDeals, useDeleteDeal, usePipelines } from './api';
import { DEAL_TYPE_LABELS } from './labels';
import { DealDialog } from './deal-dialog';
import type { Deal, DealStatus } from './types';

const STATUS_VARIANT: Record<DealStatus, 'default' | 'success' | 'destructive'> = {
  OPEN: 'default',
  WON: 'success',
  LOST: 'destructive',
};

export function DealsListPage() {
  const t = useT();
  const navigate = useNavigate();
  const { can } = usePermission();
  const { data: me } = useSession();
  const [status, setStatus] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [creating, setCreating] = useState(false);
  const remove = useDeleteDeal();
  const list = useRecordList(
    'deal',
    [
      { key: 'all', label: 'All deals' },
      { key: 'mine', label: 'My deals', ownerId: 'me' },
      { key: 'open', label: 'Open', filters: [{ field: 'status', op: 'in', value: ['OPEN'] }] },
      { key: 'closing', label: 'Closing this month', filters: [{ field: 'status', op: 'in', value: ['OPEN'] }, { field: 'expectedCloseDate', op: 'in_next_days', value: 30 }] },
      { key: 'won', label: 'Won', filters: [{ field: 'status', op: 'in', value: ['WON'] }] },
    ],
    me?.id,
  );
  const { data: properties = [] } = useProperties('deal');

  const debounced = useDebounce(list.search);
  const { data: pipelines = [] } = usePipelines();
  const { data, isLoading, error, refetch } = useDeals({
    ...list.params,
    q: debounced || undefined,
    status: status || undefined,
    pipelineId: pipelineId || undefined,
  });
  const rows = data?.data ?? [];

  const columns: Column<Deal>[] = [
    {
      key: 'title',
      header: 'Deal name',
      sortKey: 'title',
      cell: (row) => (
        <div className="min-w-0">
          <div className="text-link truncate font-semibold hover:underline">{row.title}</div>
          <div className="truncate text-xs text-muted-foreground">
            {row.company.name} · {row.number}
          </div>
        </div>
      ),
    },
    {
      key: 'stage',
      header: 'Deal stage',
      cell: (row) => (
        <span
          className="inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: `${row.stage.colour}1f`, color: row.stage.colour }}
        >
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: row.stage.colour }} />
          {row.stage.name}
        </span>
      ),
    },
    {
      key: 'value',
      header: 'Amount',
      sortKey: 'value',
      className: 'tabular text-sm font-semibold',
      cell: (row) => formatMoney(row.value, row.currency),
    },
    {
      key: 'weighted',
      header: 'Weighted amount',
      className: 'tabular text-sm text-muted-foreground',
      cell: (row) => formatMoney(row.weightedValue, row.currency),
    },
    {
      key: 'close',
      header: 'Close date',
      sortKey: 'expected_close_date',
      className: 'whitespace-nowrap text-sm',
      cell: (row) => formatDate(row.expectedCloseDate),
    },
    {
      key: 'type',
      header: 'Deal type',
      className: 'text-sm text-muted-foreground',
      cell: (row) => DEAL_TYPE_LABELS[row.dealType],
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge>
          {row.status === 'LOST' && row.lostReason && <span className="text-[10px] text-muted-foreground">{row.lostReason}</span>}
        </div>
      ),
    },
    {
      key: 'owner',
      header: 'Deal owner',
      className: 'text-sm',
      cell: (row) => row.owner?.name ?? <span className="text-muted-foreground">{t('No owner')}</span>,
    },
    ...customColumns<Deal>(properties),
  ];

  return (
    <div>
      <ObjectIndex
        title="Deals"
        count={data?.meta?.total}
        viewsNode={<ViewTabs type="deal" list={list} />}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder="Search title, number or company"
        filters={
          <>
            <FilterPill
              label="Pipeline"
              value={pipelineId}
              onChange={(value) => {
                setPipelineId(value);
                list.setPage(1);
              }}
              options={pipelines.map((pipeline) => ({ value: pipeline.id, label: pipeline.name }))}
            />
            <FilterPill
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                list.setPage(1);
              }}
              options={[
                { value: 'OPEN', label: t('Open') },
                { value: 'WON', label: t('Won') },
                { value: 'LOST', label: t('Lost') },
              ]}
            />
            <FilterButton type="deal" list={list} />
          </>
        }
        hasFilters={Boolean(status || pipelineId || list.extra.conditions.length)}
        onClearFilters={() => {
          setStatus('');
          setPipelineId('');
          list.setExtra({ match: 'ALL', conditions: [] });
        }}
        toolbarEnd={<ExportViewButton type="deal" list={list} />}
        mode="table"
        tableHref="/crm/deals/list"
        boardHref="/crm/deals"
        calendarHref="/crm/deals/calendar"
        actions={
          <Can permission="crm.deal.create">
            <AddButton onClick={() => setCreating(true)} label="Add deal" />
          </Can>
        }
      >
        <DataTable
          bare
          storageKey="deals"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          error={error ? 'Could not load deals.' : null}
          onRetry={() => void refetch()}
          meta={data?.meta}
          onPageChange={list.setPage}
          onLimitChange={list.setLimit}
          onRowClick={(row) => navigate(`/crm/deals/${row.id}`)}
          sort={list.sort}
          onSortChange={list.setSort}
          bulkActions={(ids, clear) => (
            <>
              <BulkMenu type="deal" ids={ids} onDone={clear} />
              <BulkExport
                rows={rows}
                ids={ids}
                rowKey={(row) => row.id}
                filename="deals"
                fields={[
                  { header: 'Deal', value: (row) => row.title },
                  { header: 'Number', value: (row) => row.number },
                  { header: 'Company', value: (row) => row.company.name },
                  { header: 'Stage', value: (row) => row.stage.name },
                  { header: 'Amount', value: (row) => formatMoney(row.value, row.currency) },
                  { header: 'Close date', value: (row) => formatDate(row.expectedCloseDate) },
                  { header: 'Status', value: (row) => row.status },
                  { header: 'Owner', value: (row) => row.owner?.name },
                ]}
              />
              {can('crm.deal.delete') && (
                <BulkDelete ids={ids} onDone={clear} remove={(id) => remove.mutateAsync(id)} noun="deals" />
              )}
            </>
          )}
          emptyTitle="No deals yet"
          emptyDescription="Create one, or convert a lead to start a pipeline."
          emptyAction={
            <Can permission="crm.deal.create">
              <Button onClick={() => setCreating(true)}>
                <Briefcase className="h-4 w-4" />
                {t('Add deal')}
              </Button>
            </Can>
          }
        />
      </ObjectIndex>

      {creating && <DealDialog onClose={() => setCreating(false)} />}
    </div>
  );
}
